import { HotelOption } from '../types/security';

/**
 * RDC provinces with their cities.
 * Source: 26 provinces of the Democratic Republic of Congo.
 */
export const RDC_GEO_DATA: Record<string, string[]> = {
  'Kinshasa': ['Kinshasa'],
  'Kongo-Central': ['Matadi', 'Boma', 'Muanda', 'Mbanza-Ngungu'],
  'Kwango': ['Kenge', 'Kahemba'],
  'Kwilu': ['Bandundu', 'Kikwit', 'Idiofa'],
  'Mai-Ndombe': ['Inongo', 'Kutu', 'Bolobo'],
  'Équateur': ['Mbandaka', 'Bikoro', 'Basankusu'],
  'Mongala': ['Lisala', 'Bumba'],
  'Nord-Ubangi': ['Gbadolite', 'Mobayi-Mbongo'],
  'Sud-Ubangi': ['Gemena', 'Libenge', 'Zongo'],
  'Tshuapa': ['Boende', 'Befale'],
  'Tshopo': ['Kisangani', 'Isangi', 'Yangambi'],
  'Bas-Uélé': ['Buta', 'Aketi'],
  'Haut-Uélé': ['Isiro', 'Dungu', 'Watsa'],
  'Ituri': ['Bunia', 'Aru', 'Mahagi'],
  'Nord-Kivu': ['Goma', 'Butembo', 'Beni'],
  'Sud-Kivu': ['Bukavu', 'Uvira', 'Baraka'],
  'Maniema': ['Kindu', 'Kasongo'],
  'Haut-Katanga': ['Lubumbashi', 'Likasi', 'Kipushi', 'Kolwezi'],
  'Lualaba': ['Kolwezi', 'Dilolo', 'Fungurume'],
  'Haut-Lomami': ['Kamina', 'Kabongo'],
  'Tanganyika': ['Kalemie', 'Kongolo', 'Moba'],
  'Lomami': ['Kabinda', 'Mwene-Ditu'],
  'Kasaï-Oriental': ['Mbuji-Mayi', 'Tshilenge'],
  'Kasaï-Central': ['Kananga', 'Luebo'],
  'Kasaï': ['Tshikapa', 'Ilebo'],
  'Sankuru': ['Lodja', 'Lusambo'],
};

/**
 * Returns the sorted list of all RDC provinces.
 */
export function getProvinces(): string[] {
  return Object.keys(RDC_GEO_DATA).sort();
}

/**
 * Returns the cities for a given province.
 * Returns empty array if province is not found or empty.
 */
export function getCitiesForProvince(province: string): string[] {
  if (!province || !province.trim()) return [];
  return (RDC_GEO_DATA[province] || []).slice().sort();
}

/**
 * Represents the state of the cascade geo filter.
 */
export interface GeoFilterState {
  province: string;
  city: string;
  hotelId: string;
  availableCities: string[];
  availableHotels: HotelOption[];
}

/**
 * Returns the initial (empty) geo filter state.
 */
export function initialGeoFilterState(): GeoFilterState {
  return {
    province: '',
    city: '',
    hotelId: '',
    availableCities: [],
    availableHotels: [],
  };
}

/**
 * Pure function: applies a province change to the geo filter state.
 * When province changes, city and hotel are reset, and cities are loaded from the data.
 */
export function applyProvinceChange(province: string): GeoFilterState {
  if (!province) {
    return initialGeoFilterState();
  }
  return {
    province,
    city: '',
    hotelId: '',
    availableCities: getCitiesForProvince(province),
    availableHotels: [],
  };
}

/**
 * Pure function: applies a city change to the geo filter state.
 * When city changes, hotel is reset. Province and cities remain unchanged.
 * Hotels will be loaded asynchronously by the component.
 */
export function applyCityChange(currentState: GeoFilterState, city: string): GeoFilterState {
  return {
    ...currentState,
    city,
    hotelId: '',
    availableHotels: [], // will be populated by API call
  };
}

/**
 * Pure function: applies a hotel change to the geo filter state.
 */
export function applyHotelChange(currentState: GeoFilterState, hotelId: string): GeoFilterState {
  return {
    ...currentState,
    hotelId,
  };
}
